import { useCallback, useEffect, useState } from "react";
import { Button, Card, Spinner, Stack, Text } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";

interface PreviewData {
  readonly template: string;
  readonly subject: string;
  readonly html: string;
  readonly text: string;
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; templates: readonly string[] }
  | { kind: "error"; message: string };

export function AdminEmailsTemplatesPage() {
  usePageTitle("Admin · Email templates");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [selected, setSelected] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [view, setView] = useState<"html" | "text">("html");

  useEffect(() => {
    fetch("/api/admin/emails/templates")
      .then(async (res) => {
        if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
        if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
        const json = await res.json() as { success: boolean; data?: { templates: string[] } };
        if (!json.success || !json.data) { setState({ kind: "error", message: "Unexpected response" }); return; }
        setState({ kind: "ready", templates: json.data.templates });
        if (json.data.templates.length > 0) setSelected(json.data.templates[0]);
      })
      .catch(() => setState({ kind: "error", message: "Network error" }));
  }, []);

  const loadPreview = useCallback(async (name: string) => {
    setPreview(null);
    try {
      const res = await fetch(`/api/admin/emails/templates/${name}`);
      if (!res.ok) return;
      const json = await res.json() as { success: boolean; data?: PreviewData };
      if (json.success && json.data) setPreview(json.data);
    } catch {
      // Network failures surface via the missing preview.
    }
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadPreview(selected);
  }, [selected, loadPreview]);

  if (state.kind === "unauthorized") {
    return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;
  }
  if (state.kind === "loading") {
    return <AdminPage title="Email templates"><Spinner /></AdminPage>;
  }
  if (state.kind === "error") {
    return <AdminPage title="Email templates"><div>{state.message}</div></AdminPage>;
  }

  return (
    <AdminPage title="Email templates">
      <Stack gap={4}>
        <Text color="muted" size="sm">
          Sample-data render of every template. Updates automatically when a new template is added.
        </Text>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {state.templates.map((t) => (
            <Button
              key={t}
              variant={selected === t ? "primary" : "secondary"}
              size="sm"
              onClick={() => setSelected(t)}
            >
              {t}
            </Button>
          ))}
        </div>

        {preview && (
          <Card>
            <Stack gap={3}>
              <div>
                <Text color="muted" size="sm">Subject</Text>
                <div style={{ fontWeight: 600 }}>{preview.subject}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant={view === "html" ? "primary" : "secondary"} size="sm" onClick={() => setView("html")}>HTML</Button>
                <Button variant={view === "text" ? "primary" : "secondary"} size="sm" onClick={() => setView("text")}>Text</Button>
              </div>
              {view === "html" ? (
                <iframe
                  title={`${preview.template}-preview`}
                  srcDoc={preview.html}
                  style={{ width: "100%", minHeight: 520, border: "1px solid var(--color-border-subtle)", borderRadius: 8, background: "white" }}
                />
              ) : (
                <pre style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 13, background: "var(--color-bg-subtle)", padding: 12, borderRadius: 8, margin: 0 }}>
                  {preview.text}
                </pre>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </AdminPage>
  );
}
